// Vista previa del formulario (aproximación fiel del widget público; no envía nada).
import type { CSSProperties, JSX } from "react";
import { DEFAULT_CONSENT_TEXT, DEFAULT_STYLE, FONT_STACK, type FormDesign, type MktOptions } from "./shared.tsx";

export function FormPreview({ design, options, device = "desktop", selectedId, onSelect }: {
  design: FormDesign; options: MktOptions | undefined; device?: "desktop" | "mobile"; selectedId?: string | null; onSelect?: (id: string) => void;
}): JSX.Element {
  const st = { ...DEFAULT_STYLE, ...(design.style ?? {}) };
  const r = st.radius;
  const fieldStyle: CSSProperties = {
    width: "100%", padding: "10px 12px", border: `1px solid ${st.fieldBorder}`, borderRadius: Math.min(r, 12),
    background: st.fieldBg, color: st.fieldText, font: "inherit", fontSize: 15, boxSizing: "border-box",
  };
  const fieldMeta = (key?: string) => options?.fields.find((f) => f.key === key);
  const mobile = device === "mobile";
  const privacy = design.privacy ?? {};

  return (
    <div className="flex justify-center py-6 px-3 rounded-md bg-canvas overflow-auto">
      <div style={{ width: mobile ? 375 : "100%", maxWidth: mobile ? 375 : st.maxWidth }}>
        <div style={{
          position: "relative", background: st.bg, color: st.text, borderRadius: r, overflow: "hidden", fontFamily: FONT_STACK[st.font] ?? FONT_STACK.system,
          fontSize: 15, lineHeight: 1.45, backgroundImage: st.bgImage ? `url(${JSON.stringify(st.bgImage)})` : undefined, backgroundSize: "cover", backgroundPosition: "center",
          boxShadow: "0 1px 3px rgba(0,0,0,.12)",
        }}>
          {st.bgImage && <div style={{ position: "absolute", inset: 0, background: st.overlayColor, opacity: st.overlay }} />}
          <div style={{ position: "relative", padding: mobile ? Math.min(st.padding, 18) : st.padding }}>
            {design.title && <h2 style={{ margin: "0 0 6px", fontSize: 22, lineHeight: 1.25, fontWeight: 700 }}>{design.title}</h2>}
            {design.subtitle && <p style={{ margin: "0 0 18px", opacity: 0.85, whiteSpace: "pre-line" }}>{design.subtitle}</p>}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
              {(design.blocks ?? []).map((b) => {
                if (b.kind === "hidden") return null;
                const half = b.width === "half" && !mobile;
                const sel = selectedId === b.id;
                const wrap: CSSProperties = { flex: half ? "1 1 calc(50% - 7px)" : "1 1 100%", minWidth: 0, outline: sel ? "2px dashed #f97316" : undefined, outlineOffset: 4, cursor: onSelect ? "pointer" : undefined };
                const click = onSelect ? () => onSelect(b.id) : undefined;
                if (b.kind === "field") {
                  const f = fieldMeta(b.field);
                  const label = <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 5 }}>{b.label || f?.label}{b.required && <span style={{ color: "#dc2626" }}> *</span>}</label>;
                  let input: JSX.Element;
                  if (f?.type === "textarea") input = <textarea readOnly placeholder={b.placeholder} style={{ ...fieldStyle, minHeight: 96 }} />;
                  else if (f?.type === "select") input = <select disabled style={fieldStyle}><option>{b.placeholder || "Selecciona…"}</option></select>;
                  else if (f?.type === "checkbox") return (
                    <div key={b.id} style={wrap} onClick={click}><label style={{ display: "flex", gap: 8, fontSize: 13 }}><input type="checkbox" readOnly /> {b.label || f.label}{b.required && " *"}</label></div>
                  );
                  else if (f?.type === "tel") {
                    const c = options?.countries.find((x) => x.iso === (b.defaultCountry ?? "CO"));
                    input = <div style={{ display: "flex", gap: 8 }}><select disabled style={{ ...fieldStyle, width: "auto" }}><option>{c ? `${c.iso} ${c.dial}` : "CO +57"}</option></select><input readOnly placeholder={b.placeholder} style={fieldStyle} /></div>;
                  } else input = <input readOnly placeholder={b.placeholder} style={fieldStyle} />;
                  return <div key={b.id} style={wrap} onClick={click}>{label}{input}</div>;
                }
                if (b.kind === "text") {
                  const size = b.size === "lg" ? { fontSize: 19, fontWeight: 600 } : b.size === "sm" ? { fontSize: 13 } : {};
                  return <div key={b.id} style={{ ...wrap, ...size, textAlign: b.align ?? "left", whiteSpace: "pre-line" }} onClick={click}>{b.text || "Texto"}</div>;
                }
                if (b.kind === "image") return (
                  <div key={b.id} style={{ ...wrap, textAlign: b.align ?? "center" }} onClick={click}>
                    {b.url ? <img src={b.url} alt={b.alt ?? ""} style={{ maxWidth: "100%", borderRadius: Math.min(r, 12) }} /> : <div style={{ padding: 24, border: "1px dashed currentColor", opacity: 0.5, textAlign: "center" }}>Imagen</div>}
                  </div>
                );
                if (b.kind === "consent") return (
                  <div key={b.id} style={wrap} onClick={click}><label style={{ display: "flex", gap: 8, fontSize: 13 }}><input type="checkbox" readOnly /> <span>{b.label || "Acepto…"} {b.link && <u>Ver más</u>}{b.required && <span style={{ color: "#dc2626" }}> *</span>}</span></label></div>
                );
                if (b.kind === "separator") return <div key={b.id} style={wrap} onClick={click}><hr style={{ border: 0, borderTop: "1px solid currentColor", opacity: 0.2, margin: "4px 0" }} /></div>;
                return null;
              })}
              {privacy.consent !== false && (
                <div style={{ flex: "1 1 100%" }}>
                  <label style={{ display: "flex", gap: 8, fontSize: 13 }}><input type="checkbox" readOnly />
                    <span>{privacy.text || DEFAULT_CONSENT_TEXT} <u>Política de privacidad</u><span style={{ color: "#dc2626" }}> *</span></span>
                  </label>
                </div>
              )}
            </div>
            <button type="button" style={{ marginTop: 18, width: "100%", padding: "12px 16px", font: "inherit", fontWeight: 600, border: 0, borderRadius: Math.min(r, 14), background: st.button, color: st.buttonText }}>
              {design.buttonLabel || "Enviar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
